import { useTranslation } from "react-i18next";
import {
  Checkbox,
  FilterableMultiSelect,
  Select,
  SelectItem,
} from "@carbon/react";
import {
  useAvailablePRCategories,
  useAvailablePRTags,
} from "../../hooks/usePurchaseRequestsV2";
import { CompanyResponse, SiteResponse, UserCompanyDetail } from "../../types";
import { BuyerViewMode, StateFilterOption } from "./purchaseRequestListTypes";
import "./purchaseRequestsV2.scss";

interface PRTag {
  id: string;
  name: string;
}

interface PRCategory {
  id: string;
  name: string;
  nameEn: string;
}

export interface PurchaseRequestListFiltersProps {
  buyerViewMode: BuyerViewMode;
  onBuyerViewModeChange: (mode: BuyerViewMode) => void;
  selectedBuyerId: string;
  onBuyerIdChange: (id: string) => void;
  buyerUsers: UserCompanyDetail[];
  companyList?: CompanyResponse[] | null;
  selectedCompanyIds: string[];
  onCompanyIdsChange: (ids: string[]) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  stateFilters: StateFilterOption[];
  selectedTagIds: string[];
  onTagIdsChange: (ids: string[]) => void;
  selectedCategoryIds: string[];
  onCategoryIdsChange: (ids: string[]) => void;
  companySites?: SiteResponse[];
  requestingSiteIdFilter: string;
  onSiteFilterChange: (siteId: string) => void;
  langEs: boolean;
}

/**
 * The Buyer/Manager/Analyst filter panel for the PR list (Task 4 Carbon migration) — rebuilt on
 * stock `Select`/`FilterableMultiSelect`/`Checkbox`/`TextInput`, mirroring
 * `containers/Materials/MaterialListFilters.tsx`'s screen-owned pattern. Replaces the pre-Carbon
 * screen's plain `<select>`s plus the two bespoke Tailwind components
 * `PurchaseRequestTagFilterV2`/`PurchaseRequestCategoryFilterV2` (now unused — same
 * `useAvailablePRTags`/`useAvailablePRCategories` hooks, fetched here instead).
 *
 * Visibility rules preserved exactly: the tag/category filters and the (buyer-unset,
 * tag-and-category-clear) site filter only renders in the items view — same
 * `buyerViewMode === ITEMS` / `selectedBuyerId === "" && !tags.length && !categories.length`
 * gates the pre-Carbon screen used. The multi-company checklist only renders when the items view
 * is active and the caller's company list has more than one company.
 */
const PurchaseRequestListFilters = ({
  buyerViewMode,
  onBuyerViewModeChange,
  selectedBuyerId,
  onBuyerIdChange,
  buyerUsers,
  companyList,
  selectedCompanyIds,
  onCompanyIdsChange,
  statusFilter,
  onStatusFilterChange,
  stateFilters,
  selectedTagIds,
  onTagIdsChange,
  selectedCategoryIds,
  onCategoryIdsChange,
  companySites,
  requestingSiteIdFilter,
  onSiteFilterChange,
  langEs,
}: PurchaseRequestListFiltersProps) => {
  const { t } = useTranslation();
  const isItemsView = buyerViewMode === BuyerViewMode.ITEMS;

  const { data: tags } = useAvailablePRTags(selectedBuyerId || undefined);
  const { data: categories } = useAvailablePRCategories(
    selectedBuyerId || undefined
  );

  const selectedTags = (tags ?? []).filter((tag: PRTag) =>
    selectedTagIds.includes(tag.id)
  );
  const selectedCategories = (categories ?? []).filter((cat: PRCategory) =>
    selectedCategoryIds.includes(cat.id)
  );

  const toggleCompany = (companyId: string) => {
    onCompanyIdsChange(
      selectedCompanyIds.includes(companyId)
        ? selectedCompanyIds.filter((id) => id !== companyId)
        : [...selectedCompanyIds, companyId]
    );
  };

  const showExtraItemFilters =
    selectedBuyerId === "" &&
    selectedTagIds.length === 0 &&
    selectedCategoryIds.length === 0;

  return (
    <div className="purchase-requests__filters">
      <div className="purchase-requests__filters-row">
        <Select
          id="pr-view-mode"
          labelText={t("purchaseRequests.view")}
          value={buyerViewMode}
          onChange={(event) =>
            onBuyerViewModeChange(event.target.value as BuyerViewMode)
          }
        >
          <SelectItem
            value={BuyerViewMode.REQUESTS}
            text={t("purchaseRequests.viewByRequests")}
          />
          <SelectItem
            value={BuyerViewMode.ITEMS}
            text={t("purchaseRequests.viewByItems")}
          />
        </Select>

        <Select
          id="pr-buyer-filter"
          labelText={t("purchaseRequests.filters.assignedBuyer")}
          value={selectedBuyerId}
          onChange={(event) => onBuyerIdChange(event.target.value)}
        >
          <SelectItem value="" text={t("purchaseRequests.filters.allUsers")} />
          {buyerUsers.map((u) => (
            <SelectItem key={u.id} value={u.id} text={u.fullName} />
          ))}
        </Select>

        <Select
          id="pr-status-filter"
          labelText={t("purchaseRequests.filters.state")}
          value={statusFilter}
          onChange={(event) => onStatusFilterChange(event.target.value)}
        >
          {stateFilters.map((filter) => (
            <SelectItem
              key={filter.key}
              value={filter.key}
              text={filter.label}
            />
          ))}
        </Select>
      </div>

      {isItemsView && companyList && companyList.length > 1 ? (
        <fieldset className="purchase-requests__company-filter">
          <legend>{t("purchaseRequests.filters.companies")}</legend>
          {companyList.map((company) => (
            <Checkbox
              key={company.id}
              id={`pr-company-${company.id}`}
              labelText={company.name}
              checked={selectedCompanyIds.includes(company.id)}
              onChange={() => toggleCompany(company.id)}
            />
          ))}
        </fieldset>
      ) : null}

      {isItemsView ? (
        <div className="purchase-requests__filters-row">
          {tags && tags.length > 0 ? (
            <FilterableMultiSelect
              id="pr-tags-filter"
              titleText={t("purchaseRequests.filters.tags")}
              placeholder={t("purchaseRequests.filters.searchTags")}
              items={tags}
              itemToString={(tag: PRTag) => tag?.name ?? ""}
              selectedItems={selectedTags}
              onChange={({ selectedItems }) =>
                onTagIdsChange(
                  (selectedItems ?? []).map((tag: PRTag) => tag.id)
                )
              }
            />
          ) : null}

          {categories && categories.length > 0 ? (
            <FilterableMultiSelect
              id="pr-categories-filter"
              titleText={t("purchaseRequests.filters.categories")}
              placeholder={t("purchaseRequests.filters.searchCategories")}
              items={categories}
              itemToString={(cat: PRCategory) =>
                (langEs ? cat?.name : cat?.nameEn) ?? ""
              }
              selectedItems={selectedCategories}
              onChange={({ selectedItems }) =>
                onCategoryIdsChange(
                  (selectedItems ?? []).map((cat: PRCategory) => cat.id)
                )
              }
            />
          ) : null}

          {showExtraItemFilters ? (
            <>
              <Select
                id="pr-site-filter"
                labelText={t("purchaseRequests.items.columns.requestingCenter")}
                value={requestingSiteIdFilter}
                onChange={(event) => onSiteFilterChange(event.target.value)}
              >
                <SelectItem
                  value=""
                  text={t("purchaseRequests.filters.allSites")}
                />
                {(companySites ?? []).map((site) => (
                  <SelectItem
                    key={site.id}
                    value={site.id}
                    text={
                      site.internalCode
                        ? `${site.internalCode} — ${site.name}`
                        : site.name
                    }
                  />
                ))}
              </Select>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default PurchaseRequestListFilters;
